import { Loading, useMedplum } from '@medplum/react';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { mapQuestionnaireResponseItemToQuestionnaireItem } from '@utils/mapQuestionnaireResponseItemToQuestionnaireItem';
import { IconCircleOff } from '@tabler/icons-react';
import { normalizeErrorString } from '@medplum/core';
import { Container, Paper } from '@mantine/core';
import { QuestionnaireForm } from '@medplum-components/QuestionnaireForm/QuestionnaireForm';
import { useJwtApi } from '@hooks/useJwtApi';
import { ROUTES } from '@consts/routes.consts';

// -----------------------------------------------------------------------------
export const ClientApplicationForm: FC = () => {
  const medplum = useMedplum();
  const navigate = useNavigate();

  const { verifyToken } = useJwtApi();

  const [searchParams] = useSearchParams();
  const [questionnaireResponseId, setQuestionnaireResponseId] = useState<
    string | null
  >(null);

  // ---------------------------------------------------------------------------
  const token = searchParams.get('token');
  useEffect(() => {
    if (!token) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: 'Token not found',
      });

      navigate(`/${ROUTES.UNAUTHORIZED}`);
      return;
    }

    verifyToken(token)
      .then((res) => {
        if (!res?.valid) {
          notifications.show({
            color: 'red',
            icon: <IconCircleOff />,
            title: 'Error',
            message: res.error,
          });

          navigate(`/${ROUTES.UNAUTHORIZED}`);
          return;
        }

        if (!res.payload?.formId) {
          notifications.show({
            color: 'red',
            icon: <IconCircleOff />,
            title: 'Error',
            message: 'Form ID not found',
          });

          navigate(`/${ROUTES.NOT_FOUND}`);
          return;
        }

        setQuestionnaireResponseId(res.payload?.formId);
      })
      .catch((err) => {
        notifications.show({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'Error',
          message: normalizeErrorString(err),
        });

        navigate(`/${ROUTES.UNAUTHORIZED}`);
      });
  }, [navigate, token, verifyToken]);

  if (!questionnaireResponseId) {
    return <Loading />;
  }

  // ---------------------------------------------------------------------------
  const questionnaireResponse = medplum
    .searchOne('QuestionnaireResponse', {
      _id: questionnaireResponseId,
    })
    .read();

  if (!questionnaireResponse) {
    navigate(`/${ROUTES.NOT_FOUND}`);
    return null;
  }

  // ---------------------------------------------------------------------------
  const questionnaire = medplum
    .searchOne('Questionnaire', {
      _id: questionnaireResponse.questionnaire,
    })
    .read();

  if (!questionnaire) {
    navigate(`/${ROUTES.NOT_FOUND}`);
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <Container>
      <Paper p="md" my="lg" radius="md">
        <QuestionnaireForm
          questionnaire={{
            ...questionnaire,
            item: mapQuestionnaireResponseItemToQuestionnaireItem(
              questionnaire.item,
              questionnaireResponse.item
            ),
          }}
          onSubmit={async (formData: any) => {
            try {
              await medplum.updateResource({
                ...formData,
                id: questionnaireResponse.id,
                subject: questionnaireResponse.subject,
                status: 'completed',
              });

              navigate(`/${ROUTES.SUCCESS}`);
            } catch (error) {
              notifications.show({
                color: 'red',
                icon: <IconCircleOff />,
                title: 'Error',
                message: normalizeErrorString(error),
              });
            }
          }}
        />
      </Paper>
    </Container>
  );
};
